<script>
import Layout from '@layouts/main'
import * as models from './models'
import api from '@utils/api'
import editItem from './item-edit'
import newItem from './item-new'
import deleteItem from '@views/delete-item-modal'
import Filters from '@views/filters-dialog'
import TablePaginator from '@components/paginator'
import ButtonExport from '@components/buttons/dictionary-export'
import ButtonImport from '@components/buttons/dictionary-import'
import userHasRolesToWrite from '@utils/userPermissions'

export default {
  name: 'Dictionary',
  components: {
    Layout,
    editItem,
    newItem,
    deleteItem,
    Filters,
    TablePaginator,
    ButtonExport,
    ButtonImport,
  },
  data() {
    return {
      isLoading: false,
      rowsPerPage: [10, 25, 50],
      rolesAllowedToWrite: ['SUPERUSER', 'DICTIONARIES_OPERATOR'],
      page: 1,
      totalPages: 0,
      sortBy: 'name',
      descending: false,
      first: false,
      last: false,
      content: [],
      pagination: {},
      newItem: false,
      editItem: false,
      currentItem: {},
      showFilters: false,
      filters: null,
      fab: false,
      selected: [],
    }
  },
  computed: {
    model() {
      return models[this.$route.params.dictionary].filter(x => x.type !== 'id')
    },
    id() {
      return models[this.$route.params.dictionary].find(x => x.type === 'id')
    },
    headers() {
      let headers = this.model.map(item => {
        return {
          text: item.name,
          value: item.code,
          sortable: true,
        }
      })
      if (
        userHasRolesToWrite(
          this.$store.state.auth.userRole,
          this.rolesAllowedToWrite
        )
      ) {
        headers.push({ text: 'actions', value: 'actions', sortable: false })
      }
      return headers
    },
    params() {
      return {
        size: this.pagination.rowsPerPage || null,
        page: this.page || null,
        sort_property: this.sortBy || null,
        sort_direction: this.descending,
        ...this.filters,
      }
    },
    userAllowedToCreate() {
      return userHasRolesToWrite(
        this.$store.state.auth.userRole,
        this.rolesAllowedToWrite
      )
    },
    canExport() {
      const currentDict = this.$route.params.dictionary
      const availableDict = ['communicationtype', 'city', 'region']
      return availableDict.includes(currentDict)
    },
  },
  watch: {
    pagination: {
      handler() {
        this.$store.commit('pagination/SET', {
          name: this.$route.params.dictionary,
          pagination: {
            page: this.page,
            rowsPerPage: this.pagination.rowsPerPage,
            sortBy: this.sortBy,
            descending: this.descending,
          },
        })
      },
      deep: true,
    },
  },
  created() {
    this.getPaginator()
    this.getFilters()
  },
  methods: {
    fetchData() {
      this.loading = true
      api
        .getItems(this.$route.params.dictionary, this.params)
        .then(response => {
          this.content = response.content
          this.totalPages = response.totalPages
          this.first = response.first
          this.last = response.last
          this.page = response.number + 1
          this.pagination = {
            ...this.pagination,
            page: 1,
            rowsPerPage: response.size,
            sortBy: this.sortBy,
            descending: this.descending,
          }
          this.isLoading = false
        })
    },
    getValue(dictionaryName, value) {
      return this.$store.getters['data/getValueFromDictionary'](
        dictionaryName,
        value
      )
    },
    openItem(item) {
      this.currentItem = item
      this.editItem = true
    },
    closeItem() {
      this.currentItem = {}
      this.editItem = false
    },
    itemWasUpdated() {
      this.currentItem = {}
      this.editItem = false
      this.fetchData(this.params)
    },
    getFilters() {
      const filter = this.$store.getters['filters/getFilters'](
        this.$route.params.dictionary
      )
      this.filters = filter ? filter.value : null
      this.fetchData()
    },
    setFilters() {
      const filter = this.$store.getters['filters/getFilters'](
        this.$route.params.dictionary
      )
      this.filters = filter ? filter.value : null
      this.showFilters = false
      this.page = 1
      this.fetchData()
    },
    getPaginator() {
      const pagination = this.$store.getters['pagination/get'](
        this.$route.params.dictionary
      )
      if (pagination) {
        this.page = pagination.page || 1
        this.sortBy = pagination.sortBy || this.sortBy
        this.descending = pagination.descending || this.descending
        this.pagination.page = 1
        this.pagination.rowsPerPage = pagination.rowsPerPage || 10
      } else {
        this.page = 1
        this.pagination.page = 1
        this.pagination.rowsPerPage = 10
      }
    },
    changePage(value) {
      this.page = value
      this.fetchData()
    },
    changeRowsPerPage(value) {
      if (this.pagination.rowsPerPage === value) return
      this.pagination = {
        ...this.pagination,
        rowsPerPage: value,
      }
      this.page = 1
      this.$store.dispatch('pagination/saveRowsPerPage', value)
      this.fetchData()
    },
    changeSort(header) {
      if (!header.sortable) return
      if (this.sortBy === header.value) {
        this.descending = !this.descending
      } else {
        this.sortBy = header.value
        this.descending = false
      }
      this.fetchData()
    },
    getSortClass(header) {
      return [
        header.sortable ? 'column sortable' : null,
        this.pagination.descending ? 'desc' : 'asc',
        header.value === this.pagination.sortBy ? 'active' : '',
      ]
    },
  },
}
</script>

<template>
  <Layout>
    <div slot="actionButton">
      <div class="hidden-xs-only">
        <template v-if="canExport">
          <button-export
            :items="selected"
            :entity="this.$route.params.dictionary"
          />
          <button-export 
            :all="true" 
            :entity="this.$route.params.dictionary" />
          <button-import
            :entity="this.$route.params.dictionary"
            @successfully="fetchData()"
          />
        </template>
        <v-btn 
          color="info" 
          @click="fetchData">
          <v-icon>refresh</v-icon>
        </v-btn>
        <v-btn
          :color="filters ? `success` : `info`"
          @click="showFilters = true"
        >
          <v-icon>filter_list</v-icon>
        </v-btn>
        <v-btn
          v-if="userAllowedToCreate"
          slot="activator"
          color="primary"
          dark
          @click="newItem = true"
        >
          <v-icon>add</v-icon>
        </v-btn>
      </div>
      <div class="hidden-sm-and-up">
        <v-speed-dial
          v-model="fab"
          direction="left"
          transition="slide-y-reverse-transition"
        >
          <v-btn
            slot="activator"
            v-model="fab"
            color="blue darken-2"
            dark
            fab
            small
            flat
          >
            <v-icon>more_vert</v-icon>
            <v-icon>close</v-icon>
          </v-btn>
          <v-btn
            v-if="userAllowedToCreate"
            color="primary"
            dark
            small
            fab
            @click="newItem = true"
          >
            <v-icon>add</v-icon>
          </v-btn>
          <v-btn
            :color="filters ? `success` : `info`"
            small
            fab
            @click="showFilters = true"
          >
            <v-icon>filter_list</v-icon>
          </v-btn>
          <v-btn 
            color="info" 
            small 
            fab 
            @click="fetchData">
            <v-icon>refresh</v-icon>
          </v-btn>
        </v-speed-dial>
      </div>
    </div>
    <v-data-table
      v-model="selected"
      :headers="headers"
      :items="content"
      :pagination="pagination"
      :loading="isLoading"
      :class="$style.sdTable"
      item-key="code"
      hide-actions
    >
      <template 
        slot="headers" 
        slot-scope="props">
        <tr>
          <th v-if="canExport" />
          <th
            v-for="header in headers"
            :key="header.text"
            :class="getSortClass(header)"
            style="text-align: left"
            @click="changeSort(header)"
          >
            {{ $t(`label__${header.text}`) }}
            <v-icon 
              v-if="header.sortable" 
              small>expand_less</v-icon>
          </th>
        </tr>
      </template>
      <v-progress-linear
        slot="progress"
        height="2"
        color="blue"
        background-color="#E0E0E0"
        indeterminate
      />
      <template 
        slot="items" 
        slot-scope="props">
        <tr 
          :class="$style.sdEditRow" 
          @click="openItem(props.item)">
          <td 
            v-if="canExport" 
            style="width: 70px">
            <v-checkbox
              v-model="props.selected"
              primary
              hide-details
              @click.stop
            />
          </td>
          <td 
            v-for="item in model" 
            :key="item.code">
            {{
              item.dictionary
                ? getValue(item.dictionary, props.item[item.code])
                : props.item[item.code]
            }}
          </td>
          <td 
            v-if="userAllowedToCreate" 
            style="width: 70px">
            <delete-item
              :item-id="props.item[id.code]"
              :table-name="$route.params.dictionary"
              @item-deleted="fetchData()"
            />
          </td>
        </tr>
      </template>
      <template slot="no-data">
        <div class="text-xs-center">
          {{ $t('label__no_data_available') }}
        </div>
      </template>
    </v-data-table>
    <table-paginator
      :page="page"
      :total-pages="totalPages"
      :first="first"
      :last="last"
      :rows-per-page="pagination.rowsPerPage"
      @change-page="changePage($event)"
      @change-rows-per-page="changeRowsPerPage($event)"
    />
    <new-item
      :dialog="newItem"
      @close-modal="newItem = false"
      @item-added="fetchData()"
    />
    <edit-item
      :dialog="editItem"
      :current-item="currentItem"
      @close-modal="closeItem"
      @item-updated="itemWasUpdated"
    />
    <filters
      :visible="showFilters"
      :model="model"
      :model-name="$route.params.dictionary"
      @close-modal="showFilters = false"
      @filters-confirmed="getFilters()"
    />
  </Layout>
</template>

<style lang="scss" module>
@import '@design';
.sdTable {
  border: 1px solid #dfdfdf;

  th {
    outline: none;
  }
}
.sdEditRow {
  cursor: pointer;
}
.sdBorder {
  border: 1px solid #dfdfdf;
  border-bottom: none;
}
</style>
